import { FormProvider } from 'react-hook-form';
import { z } from 'zod';
import {
  createAllpayUserBody,
  getGetTravellerByIDQueryKey,
  getListAllpayUsersQueryKey,
  useGetDepartmentsList,
  useListAllpayUsers,
  useUpdateTravellerByID,
} from '@/api/trafikportalen';
import { useCreateAllpayUser } from '@/api/trafikportalen';
import { TravellerWithCollectionMembershipsDTO } from '@/api/trafikportalen';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from '@/hooks/useToast';
import { FormElement } from '../FormElement';
import { FormSection } from '../FormSection';
import { DepartmentPicker } from '../DepartmentPicker';
import Input from '../core/Input';
import { InputPhone } from '../Inputs/InputPhone';
import Button from '../core/Button';
import { FormSubmitActions } from '../FormSubmitActions';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { OrganizationCreditInfo } from '../OrganizationCreditInfo';
import { trimData } from '@/helpers/trimData';
import { useOrgCreditInfo } from '@/hooks/useOrgCreditInfo';
import { TravellerCreditInfo } from '../TravellerCreditInfo';
import { SelectedDepartment } from '@/types/selectedDepartment';
import { hasDepartmentWriteAccess } from '@/helpers/departmentPermissions';
import { MOBILE_PHONE_REGEX } from '@/helpers/validation';
import { cn } from '@/helpers/classnames';

interface CreateAllpayUserFormProps {
  traveller: TravellerWithCollectionMembershipsDTO;
  onOpenChange: (open: boolean) => void;
}

type FormView = 'details' | 'departmentPicker';

export const AllpayCreateForm = ({
  traveller,
  onOpenChange,
}: CreateAllpayUserFormProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const customErrorMap = useCustomErrorMap;
  const createAllpayUser = useCreateAllpayUser();
  const updateTraveller = useUpdateTravellerByID();

  const { remainingCredit, hasNoCreditLimit } = useOrgCreditInfo();
  const { data: allDepartments } = useGetDepartmentsList();
  const { data: allpayUsers } = useListAllpayUsers({
    traveller_id: traveller.traveller.id,
  });

  const [currentView, setCurrentView] = useState<FormView>('details');
  const [selectedDepartment, setSelectedDepartment] =
    useState<SelectedDepartment | null>(null);

  const schema = useMemo(
    () =>
      createAllpayUserBody.and(
        z.object({
          credit: z.number().gte(0, {
            message: formatMessage({
              defaultMessage: 'Kredit måste vara minst 0',
            }),
          }),
          phone_number: z.string().regex(MOBILE_PHONE_REGEX, {
            message: formatMessage({
              defaultMessage:
                'Mobilnummer måste vara + följt av exakt 11 siffror',
            }),
          }),
        }),
      ),
    [formatMessage],
  );

  type CreateAllpayUserFormType = z.infer<typeof schema>;

  const customResolver = zodResolver(schema, {
    errorMap: customErrorMap(),
  });

  const methods = useForm<CreateAllpayUserFormType>({
    defaultValues: {
      collection_member_id: '',
      phone_number: traveller.traveller.phone ?? '',
      department_id: '',
      credit: 0,
    },
    resolver: customResolver,
    mode: 'onChange',
  });

  const [creditString, setCreditString] = useState('');

  const creditError = useMemo(() => {
    if (!creditString) {
      return '';
    }
    const credit = parseInt(creditString);
    if (isNaN(credit)) {
      return formatMessage({ defaultMessage: 'Ogiltigt värde' });
    }
    if (credit < 0) {
      return formatMessage({
        defaultMessage: 'Kreditgränsen kan inte vara negativ',
      });
    }
    if (credit > 20000) {
      return formatMessage({
        defaultMessage: 'Kreditgränsen kan inte överstiga 20 000 SEK',
      });
    }
    if (!hasNoCreditLimit && credit > remainingCredit) {
      return formatMessage({
        defaultMessage:
          'Kreditgränsen kan inte överstiga organisationens kvarvarande kredit',
      });
    }
    return methods.formState.errors.credit?.message;
  }, [
    creditString,
    formatMessage,
    methods.formState.errors.credit?.message,
    remainingCredit,
    hasNoCreditLimit,
  ]);

  const cancelEdit = useCallback(() => {
    methods.reset();
    onOpenChange?.(false);
  }, [methods, onOpenChange]);

  const allowedDepartmentIds = useMemo(() => {
    const allowedDepartmentIds: string[] = [];

    traveller.memberships.forEach((membership) => {
      if (
        membership.active &&
        hasDepartmentWriteAccess(membership.department_id) &&
        !allpayUsers?.some(
          (user) => user.department_id === membership.department_id,
        )
      ) {
        allowedDepartmentIds.push(membership.department_id);
      }
    });

    return [...new Set(allowedDepartmentIds)];
  }, [traveller.memberships, allpayUsers]);

  const setDepartment = useCallback(
    (department: SelectedDepartment) => {
      const collectionMember = traveller.memberships.find(
        (membership) => membership.department_id === department.id,
      );
      if (!collectionMember) {
        return;
      }
      setSelectedDepartment(department);
      methods.setValue('department_id', department.id);
      methods.setValue(
        'collection_member_id',
        collectionMember.collection_member_id,
      );
      methods.trigger();
    },
    [methods, traveller.memberships],
  );

  useEffect(() => {
    if (allDepartments && allowedDepartmentIds.length > 0) {
      const department = allDepartments.find(
        (department) => department.id === allowedDepartmentIds[0],
      );
      if (department) {
        setDepartment(department);
      }
    }
  }, [allowedDepartmentIds, allDepartments, setDepartment]);

  const handleDepartmentSelect = useCallback(
    (department: SelectedDepartment) => {
      const collectionMember = traveller.memberships.find(
        (membership) => membership.department_id === department.id,
      );
      if (!collectionMember) {
        return;
      }

      setSelectedDepartment(department);
      setCurrentView('details');
      setDepartment(department);
    },
    [traveller.memberships, setDepartment],
  );

  const onSubmit: SubmitHandler<CreateAllpayUserFormType> = useCallback(
    (data) => {
      createAllpayUser.mutate(
        {
          data: trimData(data),
        },
        {
          onSuccess: async () => {
            toast.success(
              {
                title: formatMessage({
                  defaultMessage: 'Tjänsteresekredit uppdaterad',
                }),
              },
              { autoClose: 3000 },
            );
            await queryClient.invalidateQueries({
              queryKey: getListAllpayUsersQueryKey(),
            });
            cancelEdit();

            updateTraveller.mutate(
              {
                travellerID: traveller.traveller.id,
                data: trimData({
                  ...traveller.traveller,
                  phone: data.phone_number,
                }),
              },
              {
                onSuccess: async () => {
                  await queryClient.invalidateQueries({
                    queryKey: getGetTravellerByIDQueryKey(
                      traveller.traveller.id,
                    ),
                  });
                },
              },
            );
          },
          onError: (error) => {
            console.error('Error: ', error);
            toast.error(
              {
                title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
                description: error.message,
              },
              { autoClose: 3000 },
            );
            return;
          },
        },
      );
    },
    [
      createAllpayUser,
      formatMessage,
      queryClient,
      cancelEdit,
      updateTraveller,
      traveller.traveller,
    ],
  );

  if (currentView === 'departmentPicker') {
    return (
      <div className="flex flex-col items-center gap-2">
        <DepartmentPicker
          title={<FormattedMessage defaultMessage="Välj betalande avdelning" />}
          suggestedDepartmentId={selectedDepartment?.id}
          onSelect={handleDepartmentSelect}
          enabledDepartments={allowedDepartmentIds}
        />
        <p className="text-text-700">
          <FormattedMessage defaultMessage="Resenären måste vara medlem i avdelningen som ska betala för krediten." />
        </p>
        <Button variant="ghost" onClick={() => setCurrentView('details')}>
          <FormattedMessage defaultMessage="Avbryt" />
        </Button>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">
          <OrganizationCreditInfo />
          <TravellerCreditInfo traveller={traveller} />
          <FormSection editMode={true}>
            <FormElement
              id="credit"
              label={<FormattedMessage defaultMessage="Kredit" />}
              input={
                <Input
                  id="credit"
                  type="text"
                  value={creditString}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (value !== '0') {
                      value = value.replace(/^0+/, '');
                    }
                    setCreditString(value);
                    const creditValue = parseInt(value);
                    if (!isNaN(creditValue)) {
                      methods.setValue('credit', creditValue);
                      methods.trigger();
                    }
                  }}
                  error={creditError}
                  onBlur={() =>
                    setCreditString(methods.getValues('credit').toString())
                  }
                  endAdornment={<FormattedMessage defaultMessage="SEK" />}
                />
              }
            />
            <FormElement
              id="phone_number"
              label={<FormattedMessage defaultMessage="Mobilnummer" />}
              input={
                <InputPhone
                  name="phone_number"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.phone_number}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              label={<FormattedMessage defaultMessage="Betalande avdelning" />}
              button={
                <Button
                  className={cn(
                    'w-full',
                    selectedDepartment?.name &&
                      'justify-start truncate text-left',
                  )}
                  onClick={() => setCurrentView('departmentPicker')}
                >
                  <p className="truncate">
                    {selectedDepartment?.name ?? 'Välj en avdelning'}
                  </p>
                </Button>
              }
              input={null}
            />
            <p className="mt-3 text-text-800">
              <FormattedMessage defaultMessage="Personen får ett sms med informatiom om hur de aktiverar tjänsteresa i appen." />
            </p>
          </FormSection>
        </div>
        <FormSubmitActions
          active={true}
          createMode={true}
          onCancel={cancelEdit}
          disabled={!methods.formState.isValid}
          onSubmit={methods.handleSubmit(onSubmit)}
          isLoading={createAllpayUser.isPending || updateTraveller.isPending}
        />
      </form>
    </FormProvider>
  );
};
